import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { resolveNotifRoute } from '../../utils/notifRouteResolver';

function NotificationBell({ iconStyle, onNavigate, onMarkAllRead, onRefresh }) {
  const { user } = useAuth();
  const role = user?.role?.toLowerCase() || 'employee';

  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const pollRef = useRef(null);

  const fetchNotifs = useCallback(async () => {
    try { const data = await api.getNotifications(); setNotifications(data || []); }
    catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchNotifs();
    pollRef.current = setInterval(fetchNotifs, 15000);
    return () => clearInterval(pollRef.current);
  }, [fetchNotifs]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const unread = notifications.filter(n => !n.is_read).length;

  const markRead = async (id) => {
    try {
      await api.markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      if (onRefresh || onMarkAllRead) (onRefresh || onMarkAllRead)();
    } catch (e) { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      setLoading(true);
      await api.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      if (onMarkAllRead || onRefresh) (onMarkAllRead || onRefresh)();
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };

  const handleNotifClick = async (n) => {
    if (!n.is_read) await markRead(n.id);
    if (!onNavigate) return;
    const tab = resolveNotifRoute(n, role);
    onNavigate(tab);
    setOpen(false);
  };

  const fmtTime = (t) => {
    if (!t) return '';
    try {
      const d = new Date(t); const now = new Date();
      const diff = Math.floor((now - d) / 60000);
      if (diff < 1) return 'Just now';
      if (diff < 60) return `${diff}m ago`;
      if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    } catch { return ''; }
  };

  const colorMap = {
    chat:'#0ea5e9', leave:'#f59e0b', permission:'#f59e0b', payroll:'#16a34a',
    helpdesk:'#ef4444', tickets:'#ef4444', requests:'#ef4444', announcement:'#8b5cf6',
    announcements:'#8b5cf6', announce:'#8b5cf6', onboarding:'#6366f1', event:'#3b82f6',
    calendar:'#3b82f6', asset:'#d97706', assets:'#d97706', lost_asset:'#dc2626',
    evaluation:'#7c3aed', project:'#0284c7', system:'#64748b', general:'#64748b',
    offboarding:'#dc2626', exit:'#dc2626', maintenance:'#d97706', reports:'#16a34a',
    advances:'#059669', permission:'#f59e0b', permissions:'#f59e0b',
  };
  const labelMap = {
    chat:'Chat', leave:'Leave', permission:'Permission', payroll:'Payroll',
    helpdesk:'HelpDesk', tickets:'Tickets', requests:'IT Req',
    announcement:'Announce', announcements:'Announce', announce:'Announce',
    onboarding:'Onboard', offboarding:'Offboard', calendar:'Calendar',
    event:'Event', reminder:'Reminder', asset:'Asset', assets:'Assets',
    lost_asset:'Lost Asset', evaluation:'Eval', project:'Project', reports:'Reports',
    advances:'Advance', exit:'Exit', maintenance:'Maint', permissions:'Perms',
  };
  const getColor = (n) => colorMap[(n.type||'').toLowerCase()] || colorMap[(n.route||'').toLowerCase()] || '#6366f1';
  const getLabel = (n) => labelMap[(n.type||'').toLowerCase()] || labelMap[(n.route||'').toLowerCase()] || (n.type||'Info');

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => setOpen(!open)} style={{
        width:38, height:38, borderRadius:8, border:'1px solid #e2e8f0',
        background: open ? '#eef2ff' : '#fff',
        display:'flex', alignItems:'center', justifyContent:'center',
        cursor:'pointer', position:'relative', color: open ? '#4338ca' : '#475569',
        transition:'all 0.2s', ...(iconStyle || {})
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unread > 0 && (
          <div style={{
            position:'absolute', top:4, right:4, width: unread > 9 ? 18 : 16, height:16,
            background:'#ef4444', borderRadius:10, border:'2px solid #fff',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:8, fontWeight:700, color:'#fff'
          }}>{unread > 99 ? '99+' : unread}</div>
        )}
      </div>

      {open && (
        <div style={{
          position:'absolute', top:'100%', right:0, width:360, marginTop:6,
          background:'#fff', border:'1px solid #e2e8f0', borderRadius:12,
          boxShadow:'0 12px 36px rgba(0,0,0,0.12)', zIndex:1000, maxHeight:480,
          display:'flex', flexDirection:'column'
        }}>
          {/* Header */}
          <div style={{ padding:'12px 16px', borderBottom:'1px solid #e2e8f0',
            display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <span style={{ fontWeight:700, fontSize:14 }}>Notifications</span>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {unread > 0 && (
                <span style={{ fontSize:10, fontWeight:600, color:'#ef4444',
                  background:'#fef2f2', padding:'2px 8px', borderRadius:10 }}>{unread} new</span>
              )}
              {unread > 0 && (
                <button onClick={(e) => { e.stopPropagation(); markAllRead(); }} disabled={loading}
                  style={{ border:'none', background:'transparent', color:'#6366f1',
                    fontSize:11, fontWeight:600, cursor:'pointer', padding:'2px 4px' }}>
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Items */}
          <div style={{ flex:1, overflow:'auto', maxHeight:380 }}>
            {notifications.length === 0 ? (
              <div style={{ padding:32, textAlign:'center', color:'#94a3b8', fontSize:13 }}>
                No notifications yet
              </div>
            ) : notifications.slice(0, 25).map(n => {
              const color = getColor(n);
              const isUnread = !n.is_read;
              return (
                <div key={n.id} onClick={() => handleNotifClick(n)}
                  style={{
                    padding:'10px 16px', borderBottom:'1px solid #f8fafc',
                    cursor: onNavigate ? 'pointer' : 'default',
                    background: isUnread ? '#fafbff' : '#fff',
                    display:'flex', gap:10, alignItems:'flex-start', transition:'background 0.15s',
                  }}
                  onMouseEnter={e => { if (onNavigate) e.currentTarget.style.background='#f1f5f9'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isUnread ? '#fafbff' : '#fff'; }}>
                  <div style={{
                    width:8, height:8, borderRadius:'50%', marginTop:5, flexShrink:0,
                    background: isUnread ? color : '#e2e8f0',
                    boxShadow: isUnread ? `0 0 0 3px ${color}22` : 'none',
                  }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{
                      fontSize:12.5, fontWeight: isUnread ? 600 : 500,
                      color: isUnread ? '#0f172a' : '#64748b', lineHeight:1.4,
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                    }}>{n.title || n.message}</div>
                    {n.body && (
                      <div style={{ fontSize:11.5, color:'#94a3b8', marginTop:2,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {n.body}
                      </div>
                    )}
                    <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:4 }}>
                      <span style={{ fontSize:10, color:'#94a3b8' }}>{fmtTime(n.created_at)}</span>
                      <span style={{ fontSize:8.5, fontWeight:700, textTransform:'uppercase',
                        padding:'1px 6px', borderRadius:4,
                        background:`${color}15`, color:color, letterSpacing:0.3 }}>
                        {getLabel(n)}
                      </span>
                      {onNavigate && isUnread && (
                        <span style={{ fontSize:9, color:'#6366f1', fontWeight:600 }}>→ open</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          {onNavigate && (
            <div onClick={() => {
              const tab = resolveNotifRoute({ route:'notifications', type:'general' }, role);
              onNavigate(tab); setOpen(false);
            }}
            style={{ padding:'10px 16px', borderTop:'1px solid #f1f5f9', textAlign:'center',
              fontSize:12, color:'#6366f1', fontWeight:600, cursor:'pointer', flexShrink:0 }}
            onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
            onMouseLeave={e => e.currentTarget.style.background='#fff'}>
              View all notifications →
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
